import * as _ from 'lodash';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { first, takeUntil, tap } from 'rxjs/operators';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { deleteEvent, deleteHEvent, deleteVEvent, loadEventList, resetVEvent } from '../../features/event.actions';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { selectEventList, selectEventListSwitcher } from '../../features/event.selectors';
import { Subject } from 'rxjs';
import * as DialogActions from 'app/dialog/features/dialog.actions';
import { ToastService } from 'app/toast/services/toast.service';
import { RouteReuseStrategyService } from 'app/router-wrapper/services/route-reuse-strategy-service/route-reuse-strategy-service.service';
import { SamModalService } from 'app/event/services/sam-modal.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.scss']
})
export class EventListComponent implements OnInit, AfterViewInit, OnDestroy {

  public events$ = this.store.pipe(
    select(selectEventList),
    tap(list => {
      this.allEventCount = list.length;
      this.activeEventCount = this.getActiveEventCount(list);
    })
  );
  public switcher$ = this.store.pipe(select(selectEventListSwitcher));

  public form: UntypedFormGroup = this.formBuilder.group({
    'switcher': new UntypedFormControl(false, [])
  });;

  public eventList = [];
  public allEventCount = 0;
  public activeEventCount = 0;

  public isProduction = false;

  public takeUntilSubject: Subject<any> = new Subject();
  public takeUntilRouteReuseStrategySubject: Subject<any> = new Subject();

  constructor(
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private store: Store,
    private toastService: ToastService,
    private sharedService: SharedService,
    private routeReuseStrategyService: RouteReuseStrategyService,
    private samModalService:SamModalService
  ) {
    this.isProduction = window['isProduction'];
  }

  ngOnInit(): void {
    window['orgMain'].sendInviteModalEventType = null;  
    this.routeReuseStrategyService.status = true;
    this.routeReuseStrategyService.routeReuseStrategySubject.pipe(
      takeUntil(this.takeUntilRouteReuseStrategySubject)
    ).subscribe(() => {
      this.takeUntilSubject.next(null);
      this.takeUntilSubject.complete();
      this.takeUntilSubject = new Subject();

      this.ngAfterViewInit();
    });
  }

  ngAfterViewInit(): void {
    this.switcher$.pipe(
      first(),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      this.form.get('switcher').setValue(value);
    });

    if(this.sharedService.token) {
      this.store.dispatch({
        type: loadEventList.type,
        payload: this.sharedService.token
      });
    }
  }

  ngOnDestroy(): void {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();

    this.takeUntilRouteReuseStrategySubject.next(null);
    this.takeUntilRouteReuseStrategySubject.complete();

    this.routeReuseStrategyService.status = false;
  }

  createNewEvent() {
    this.router.navigateByUrl(`/event/select-type`)
  }

  getLinkId(link, event) {
    switch(event.type) {
      case 1:
        return `${link}_${event.virtualEventInfo.id}`;
      case 2:
        return `${link}_${event.hybridEventInfo.virtualEvent.id}`;
    }
    return `${link}_undefined`;
  }

  getEventName(event) {
    switch(event.type) {
      case 0:
        return event.inPersonEventInfo.name;
      case 1:
        return event.virtualEventInfo.name;
      case 2:
        return event.hybridEventInfo.name;
    }
    return false;
  }

  getEventImageUrl(event) {
    let imageUrl = null;
    switch(event.type) {
      case 0:
        imageUrl = event.inPersonEventInfo.image;
        break;
      case 1:
        imageUrl = event.virtualEventInfo.imageUrl;
        break;
      case 2:
        const hybridEventInfo = event.hybridEventInfo;
        imageUrl = hybridEventInfo.imageUrl || hybridEventInfo.inPersonEvent.image || hybridEventInfo.virtualEvent.imageUrl;
        break;
    }
    return imageUrl || "/images/org_logo_default_v2.svg";
  }

  getEventStatus(event) {
    switch(event.type) {
      case 0:
        return event.inPersonEventInfo.status;
      case 1:
        return event.virtualEventInfo.status;
      case 2:
        return event.hybridEventInfo.status;
    }
    return false;
  }

  getEventType(event) {
    switch(event.type) {
      case 0:
        return 'In-Person Event';
      case 1:
        return 'Virtual Event';
      case 2:
        return 'Hybrid Event';
    }
    return 'Unknown';
  }

  getEventTypeDescription(event) {
    const eventType = this.getVirtualEventType(event);
    switch(eventType) {
      case 0:
        return "People with link can register and join.";
      case 1:
        return "Guest can join by invitation only.";
    }
    return "People with link can register and join.";
  }

  getFilteredEventList(events) {
    let list = this.form.get('switcher').value ? events: _.filter(events, event => {
      switch(event.type) {
        case 0:
          return event.inPersonEventInfo.status === 0;
        case 1:
          return event.virtualEventInfo.status === 0;
        case 2:
          return event.hybridEventInfo.status === 0;
      }
      return false;
    }) || [];
    list = _.filter(list, event => {
      switch(event.type) {
        case 0:
          return event.inPersonEventInfo.isHybrid === false;
        case 1:
          return event.virtualEventInfo.isHybrid === false;
      }
      return event;
    });
    return _.sortBy(list, [function(event) { return -event.updated; }]);
  }

  getActiveEventCount(list) {
    const activeEventList = this.getFilteredEventList(list);
    return _.get(activeEventList, 'length', 0);
  }

  onEditEvent(event) {
    let name = null;
    let id = null;
    switch(event.type) {
      case 0:
        name = 'in-person';
        id = event.inPersonEventInfo.id;
        break;
      case 1:
        name = 'virtual';
        id = event.virtualEventInfo.id;
        break;
      case 2:
        name = 'hybrid';
        id = event.hybridEventInfo.id;
        break;
    }
    this.router.navigateByUrl(`event/${name}/${id}`);
  }

  deleteEvent(event, deleteValue) {
    switch(event.type) {
      case 0:
        return this.store.dispatch({
          type: deleteEvent.type,
          payload: {
            id: event.inPersonEventInfo.id,
            deleteValue
          }
        });
      case 1:
        return this.store.dispatch({
          type: deleteVEvent.type,
          payload: {
            id: event.virtualEventInfo.id,
            deleteValue
          }
        });
      case 2:
        return this.store.dispatch({
          type: deleteHEvent.type,
          payload: {
            id: event.hybridEventInfo.id,
            deleteValue
          }
        });
    }
  }

  togglePopover(popover, event) {
    if (popover.isOpen()) {
      popover.close();
    } else {
      popover.open({ event });
    }
  }

  onClickRun(event) {
    let id = null;
    switch(event.type) {
      case 1:
        id = event.virtualEventInfo.id;
        break;
      case 2:
        id = event.hybridEventInfo.virtualEvent.id;
        break;
    }
    this.router.navigateByUrl(`event/virtual/${id}/run`);
  }

  onClickEventLinks(event) {
    window['orgMain'].downloadEventQR.call(event);
  }

  getQRCode(event, type) {
    window['orgMain'].generateQRCode(event, type);
  }

  sendInvitations(event) {
    switch(event.type) {
      case 0:
        window['orgMain'].showEventInvitationModal(event.inPersonEventInfo.id, 0, 1);
        break;
      case 1:
        window['orgMain'].showEventInvitationModal(event.virtualEventInfo.id, 1, 0);
        break;
      case 2:
        window['orgMain'].showEventInvitationModal(event.hybridEventInfo.virtualEvent.id, 1, 0);
        break;
    }
  }

  getVirtualEventType(event) {
    switch(event.type) {
      case 1:
        return event.virtualEventInfo.eventType;
      case 2:
        return event.hybridEventInfo.virtualEvent.eventType;
    }
    return null;
  }

  getVirtualEventTypeString(event) {
    const eventType = this.getVirtualEventType(event);
    switch(eventType) {
      case 0:
        return 'Open Event';
      case 1:
        return 'Closed Event';
    }
    return 'Open Event';
  }

  generateConfirmAction(name, initialState) {
    return DialogActions.openDialog({
      payload: {
        name,
        options: {
          size: 'small',
          modalClass:'modal--paybee modal-sm',
          containerClass:'mdb-modal-backdrop-paybee',
          ignoreBackdropClick: true
        },
        initialState
      }
    });
  }

  onClickResetData(event) {
    const resetDataAction = this.generateConfirmAction("confirmResetAuctionData", {
      event,
      text: "This action will reset all event data",
      callback: (result) => {
        if(result == false) {
          return false;
        }

        let id = null;
        switch(event.type) {
          case 1:
            id = event.virtualEventInfo.id;
            break;
          case 2:
            id = event.hybridEventInfo.virtualEvent.id;
            break;
        }
        this.store.dispatch({
          type: resetVEvent.type,
          payload: id
        });
      }
    });
    this.store.dispatch(resetDataAction);
  }

  notify(message) {
    return this.toastService.showSuccess(message, { delay: 1000 });
  }

  onAMClick(){
    const modalRef = this.samModalService.openSAMDialog();

    modalRef.onClose.pipe().subscribe(() =>{
      this.samModalService.modalRef = null;
    })
  }

}
